import {
  GetPayrollListParams,
  GetPayrollListResponse,
} from '@nxsys/store/slices/report/types';
import axios from '../axios';
import { endPoints } from '../endpoints';
import {
  DownloadAllDraftPayrollParams,
  DownloadAllPayrollParams,
  DownloadAllQueued,
  GetDraftPayrollParams,
  GetDraftPayrollResponse,
  ReversalMultipleDraftPayrollParams,
  SendPayslipByIdParams,
} from '@nxsys/store/slices/payroll/type';
import { GetPayrollByIdResponse } from '@nxsys/store/slices/revenueFilings/types';
import {
  DraftPayrollDetailResponse,
  RecalculateParams,
  RecalculateResponse,
  SaveDraftParams,
  SaveDraftResponse,
} from '@nxsys/types/api/draft-payroll-recalc';

export const getDraftPayrollApi = async (
  params: GetDraftPayrollParams,
): Promise<GetDraftPayrollResponse> => {
  const response = await axios.get(endPoints.payroll.draft, { params });
  return response.data;
};

export const deleteDraftPayrollByIdApi = async (
  id: string,
): Promise<{ success: boolean }> => {
  const response = await axios.delete(`${endPoints.payroll.draft}/${id}`);
  return response.data;
};

export const viewDraftPayslipByIdApi = async (
  id: string,
): Promise<{ success: boolean; data: string }> => {
  const response = await axios.put(
    `${endPoints.employee.viewPdfSelfBillView}/${id}/draft`,
  );
  return response.data;
};

// NX-4971 — advisory payslip verifier: (re-)verify ONE or MANY payslip rows via
// the array-based endpoint. Confirmed rows go in payrollIds, drafts in
// draftPayrollIds (a single-element array for a per-row re-run). Returns how many
// were queued and which ids were skipped (not found / not enabled) so a bad id
// never fails the batch.
export const verifyPayslipApi = async (body: {
  draftPayrollIds?: string[];
  payrollIds?: string[];
}): Promise<{
  success: boolean;
  queued?: number;
  skipped?: { id: string; reason: string }[];
}> => {
  const response = await axios.post(`/payslip-verifier/verify`, body);
  return response.data;
};

// NX-4971 — fetch the verdict + displayable input (payslip S3 url, FPS/RPN) for ONE
// verification record, keyed by its payslip_verification `id` (the id the list badge
// points at) so the modal always matches the list row.
export const getPayslipVerificationApi = async (query: {
  id: number;
}): Promise<{ success: boolean; data: any }> => {
  const response = await axios.get(`/payslip-verifier/payslip-verification`, {
    params: query,
  });
  return response.data;
};

// NX-4971 — record a reviewer's "ground truth" verdict (required) + message (optional)
// for one verification record. Stored separately from the AI verdict.
export const setPayslipGroundTruthApi = async (body: {
  id: number;
  verdict: string;
  message?: string;
}): Promise<{ success: boolean }> => {
  const response = await axios.post(`/payslip-verifier/ground-truth`, body);
  return response.data;
};

export const downloadAllDraftPayrollApi = async (
  params: DownloadAllDraftPayrollParams,
): Promise<DownloadAllQueued> => {
  const response = await axios.post(
    endPoints.payroll.downloadAllDraft,
    {},
    { params },
  );
  return response.data;
};

export const confirmMultipleDraftPayrollApi = async (
  ids: string[],
  queryParams: { isBureau: boolean, isIreland: boolean }
): Promise<{ message: string; success: boolean }> => {
  const { isBureau, isIreland } = queryParams;
  const response = await axios.post(endPoints.report.payroll.confirm, { ids }, { params: isBureau ? { isIreland } : undefined });
  return response.data;
};

export const reversalMultipleDraftPayrollApi = async (
  params: ReversalMultipleDraftPayrollParams,
): Promise<{ success: boolean }> => {
  const response = await axios.put(
    endPoints.report.payroll.reversalPayroll,
    params,
  );
  return response.data;
};

export const getPayrollApi = async (
  params: GetPayrollListParams,
): Promise<GetPayrollListResponse> => {
  const response = await axios.get(endPoints.report.payroll.mainV2, { params });
  return response.data;
};

export const downloadAllPayrollApi = async (
  params: DownloadAllPayrollParams,
): Promise<DownloadAllQueued> => {
  const response = await axios.post(
    endPoints.report.payroll.downloadAll,
    {},
    { params },
  );
  return response.data;
};

// NX-6289: email draft payslips (Confirm Payroll / Draft Payroll) before the
// bank file. Takes draft payroll ids.
export const sendEmailDraftPayslipMultipleApi = async (
  ids: string[],
): Promise<{ success: boolean }> => {
  const response = await axios.post(endPoints.draftPayroll.email, { ids });
  return response.data;
};

export const sendEmailPayslipMultipleApi = async (
  ids: string[],
): Promise<{ success: boolean }> => {
  const response = await axios.post(endPoints.payroll.email, { ids });
  return response.data;
};

export const viewPayslipByIdApi = async (
  id: string,
): Promise<{ success: boolean; data: string }> => {
  const response = await axios.put(
    `${endPoints.employee.viewPdfSelfBillView}/${id}`,
  );
  return response.data;
};

export const sendPayslipApi = async (
  id: string,
  params: SendPayslipByIdParams,
): Promise<{ success: boolean; data: string }> => {
  const response = await axios.put(
    `${endPoints.payroll.emailManually}/${id}`,
    params,
  );
  return response.data;
};

export const safeRecMultiplePayrollApi = async (
  ids: string[],
): Promise<{ success: boolean }> => {
  const response = await axios.post(endPoints.payroll.safeRec, { ids });
  return response.data;
};

export const getPayrollByIdApi = async (
  id: string,
): Promise<GetPayrollByIdResponse> => {
  const response = await axios.get(`${endPoints.report.payroll.main}/${id}`);
  return response.data;
};

// --- Payroll Overview APIs ---

export type OverviewNextAction =
  | 'confirmPayroll'
  | 'generateBankFile'
  | 'confirmPayment'
  | 'view';

export interface PayrollOverviewGroup {
  companyId: string;
  companyName: string;
  payFrequency: string;
  taxPeriod: number;
  /** Tax month (1-12) for MONTHLY groups; null otherwise. */
  taxMonth: number | null;
  taxYear: number;
  employeeCount: number;
  estCost: number;
  progress: { label: string; done: boolean }[];
  status: string;
  nextAction: OverviewNextAction;
  draftPayrollIds: string[];
  payrollIds: string[];
}

export interface PayrollOverviewDetailData {
  companyId: string;
  companyName: string;
  payFrequency: string;
  taxPeriod: number;
  taxMonth: number | null;
  taxYear: number;
  status: string;
  pendingTimesheetCount: number;
  summary: {
    grossEarnings: number;
    netPay: number;
    totalCost: number;
    tax: number;
    employerNi: number;
    employeeNi: number;
    employerPension: number;
    employeePension: number;
    studentLoan: number;
    attachmentOrders: number;
  };
  employees: {
    draftPayrollId: string;
    subcontractorId: string;
    name: string;
    lastUpdated: string;
    grossEarnings: number;
    netPay: number;
    hasNote: boolean;
    note?: string;
    hasAlert: boolean;
    alertMessages: string[];
    pendingTimesheetCount: number;
  }[];
}

export const getPayrollOverviewListingApi = async (
  params?: { search?: string; payFrequency?: string; taxPeriod?: number; status?: string },
): Promise<{ data: PayrollOverviewGroup[]; success: boolean }> => {
  const response = await axios.get(endPoints.payrollOverview.listing, { params });
  return response.data;
};

export const getPayrollOverviewDetailApi = async (
  companyId: string,
  params: { taxPeriod: number; payFrequency: string },
): Promise<{ data: PayrollOverviewDetailData; success: boolean }> => {
  const response = await axios.get(endPoints.payrollOverview.detail(companyId), { params });
  return response.data;
};

export const getDraftPayrollDetailApi = async (
  id: string,
): Promise<DraftPayrollDetailResponse> => {
  const response = await axios.get(`${endPoints.payroll.draft}/${id}`);
  return response.data;
};

export const recalculateDraftPayrollApi = async (
  draftPayrollId: string,
  params: RecalculateParams,
): Promise<RecalculateResponse> => {
  const response = await axios.post(
    `${endPoints.payroll.draft}/${draftPayrollId}/recalculate`,
    params,
  );
  return response.data;
};

export const saveDraftPayrollApi = async (
  draftPayrollId: string,
  params: SaveDraftParams,
): Promise<SaveDraftResponse> => {
  const response = await axios.post(
    `${endPoints.payroll.draft}/${draftPayrollId}/save`,
    params,
  );
  return response.data;
};

export const rerunDraftPayrollApi = async (
  draftPayrollId: string,
): Promise<{ success: boolean }> => {
  const response = await axios.post(
    `${endPoints.payroll.draft}/${draftPayrollId}/rerun`,
  );
  return response.data;
};

export const getCachedDraftInputApi = async (
  draftPayrollId: string,
): Promise<{ success: boolean; data: any }> => {
  const response = await axios.get(
    `${endPoints.payroll.draft}/${draftPayrollId}/cache-input`,
  );
  return response.data;
};

export const setCachedDraftInputApi = async (
  draftPayrollId: string,
  input: any,
): Promise<{ success: boolean }> => {
  const response = await axios.patch(
    `${endPoints.payroll.draft}/${draftPayrollId}/cache-input`,
    input,
  );
  return response.data;
};

export const clearCachedDraftInputApi = async (
  draftPayrollId: string,
): Promise<{ success: boolean }> => {
  const response = await axios.delete(
    `${endPoints.payroll.draft}/${draftPayrollId}/cache-input`,
  );
  return response.data;
};